import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { ArrowRight, CheckCircle, Calendar } from "lucide-react";
import JourneyVisual from "./JourneyVisual";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HeroSection = ({ inquiryContext, onClearContext }) => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formTitle, setFormTitle] = useState("Explore CSR Partnership with CLiC");
  const [formData, setFormData] = useState({ 
    organization: "",
    name: "", 
    designation: "",
    email: "", 
    csrAllocation: "",
    geography: "",
    timeline: "",
    notes: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Open modal when inquiryContext is set from FeaturedStates
  useEffect(() => {
    if (inquiryContext && inquiryContext.title) {
      setFormTitle(inquiryContext.title);
      setFormData(prev => ({
        ...prev,
        geography: inquiryContext.geography || ""
      }));
      setIsModalOpen(true);
      if (onClearContext) onClearContext();
    }
  }, [inquiryContext, onClearContext]);

  const handleOpenGenericForm = () => {
    setFormTitle("Explore CSR Partnership with CLiC");
    setFormData(prev => ({ ...prev, geography: "" }));
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // Map form data to API format
      const apiData = {
        name: formData.name,
        email: formData.email,
        phone: "", // Not required in new form
        organization: formData.organization,
        role: formData.designation,
        message: `CSR Allocation: ${formData.csrAllocation}\nGeography: ${formData.geography}\nTimeline: ${formData.timeline}\nNotes: ${formData.notes}`
      };
      await axios.post(`${API}/csr-inquiries`, apiData);
      setSubmitted(true);
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error("You've already submitted an inquiry. We'll reach out soon!");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({ 
      organization: "", name: "", designation: "", email: "", 
      csrAllocation: "", geography: "", timeline: "", notes: "" 
    });
    setSubmitted(false);
    setFormTitle("Explore CSR Partnership with CLiC");
  };

  const scrollToSection = (sectionId) => {
    setIsModalOpen(false);
    setTimeout(() => {
      const element = document.querySelector(`[data-testid="${sectionId}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 300);
  };

  return (
    <section className="hero-section min-h-screen relative" data-testid="hero-section">
      {/* Background Accents */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#1F7A5A] opacity-5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 -left-20 w-72 h-72 bg-[#F4C430] opacity-5 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 lg:px-8 py-6 relative">
        {/* Header - Logo Only */}
        <header className="flex items-center justify-start mb-10">
          <div className="flex items-center gap-2" data-testid="header-logo">
            {/* Logo image */}
            <img 
              src="https://customer-assets.emergentagent.com/job_fa466eb8-6701-4b93-b151-474047be8b48/artifacts/8el55r9b_Untitled-1%20%282%29.png"
              alt="Care Leavers Inner Circle"
              className="h-12 w-auto object-contain"
            />
          </div>
        </header>

        {/* Main Hero Content - Two Column */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          
          {/* Left Content */}
          <div className="space-y-5 animate-fade-in-up">
            <h1 className="font-display text-4xl sm:text-5xl lg:text-[3.25rem] font-bold text-[#4A4A4A] leading-[1.1] tracking-tight">
              Enable Care-Leavers' Transition to{" "}
              <span className="gradient-text-green">Independence</span>
            </h1>

            <p className="text-lg text-[#4A4A4A] leading-relaxed max-w-lg">
              A structured CSR partnership for organisations investing in education, 
              employability, and youth inclusion.
            </p>

            <p className="text-[#6B7280] text-sm max-w-md">
              In a short conversation, explore CSR-aligned programs and partnership 
              models that fit your objectives.
            </p>

            {/* Primary CTA */}
            <div className="pt-2">
              <Button
                onClick={handleOpenGenericForm}
                className="btn-primary text-white font-semibold px-8 py-6 text-base rounded-lg"
                data-testid="explore-csr-btn"
              >
                Start a CSR Conversation with the Co-Founders
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <p className="text-xs text-[#9CA3AF] mt-3 max-w-md">
                A structured discussion with CLiC's co-founders to explore alignment with your organisation's CSR priorities and current allocation cycle.
              </p>
            </div>

            {/* Trust Reinforcement Line */}
            <p className="text-[11px] text-[#6B7280] pt-2">
              Supported by UNICEF · Incubated by The Nudge & Change Engine · State and Central Government-aligned implementations
            </p>

            {/* CREDIBILITY REINFORCEMENT BLOCK - Compact, below CTA */}
            <div className="pt-5 mt-2 border-t border-[#E5E7EB]" data-testid="credibility-block">
              <p className="text-xs font-semibold text-[#1F7A5A] uppercase tracking-wider mb-3">
                Care-leaver–led leadership
              </p>
              
              <div className="flex items-start gap-3">
                {/* Founders Image */}
                <div className="flex-shrink-0">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_48bcd539-ef2e-44cd-bd5d-721585fa8242/artifacts/mbkvlci8_image.png"
                    alt="Girish Mehta & Anisha Sharma - Founders, CLiC"
                    className="w-20 h-20 object-cover rounded-lg shadow-sm"
                    data-testid="founders-image"
                  />
                </div>
                
                {/* Founders Info */}
                <div className="flex-1">
                  <p className="font-semibold text-[#4A4A4A] text-sm">
                    Girish Mehta & Anisha Sharma
                  </p>
                  <p className="text-[#6B7280] text-xs mb-1">
                    Founders, CLiC (Care Leaver Inner Circle Forum)
                  </p>
                  <p className="text-[#9CA3AF] text-[10px]">
                    Both founders are care-leavers who grew up in child care institutions — bringing lived experience to the design of structured aftercare and transition systems.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Content - Live Sessions Block (moved from below) */}
          <div className="flex flex-col justify-start" data-testid="live-sessions-block">
            <p className="text-sm font-medium text-[#4A4A4A] mb-3">
              Prefer to join a live orientation session?
            </p>
            
            <div className="bg-[#1F7A5A]/5 rounded-xl p-6 border border-[#1F7A5A]/20">
              {/* Session Info */}
              <div className="mb-6">
                <p className="text-base font-semibold text-[#4A4A4A] mb-2">
                  Upcoming Live CSR Ecosystem Sessions
                </p>
                
                <div className="flex flex-wrap gap-4 mb-2">
                  <div className="flex items-center gap-2 text-sm text-[#4A4A4A]">
                    <Calendar className="w-4 h-4 text-[#1F7A5A]" />
                    <span>Fri, 20 Feb 2026</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-[#4A4A4A]">
                    <Calendar className="w-4 h-4 text-[#1F7A5A]" />
                    <span>Fri, 6 Mar 2026</span>
                  </div>
                </div>
                
                <p className="text-xs text-[#6B7280]">
                  Live on Zoom · 12:00–1:00 PM IST · For CSR Heads, CSR Committee Members, Foundations
                </p>
              </div>

              {/* Session Facilitators - Compact */}
              <div className="mb-6" data-testid="session-facilitators">
                <p className="text-xs font-semibold text-[#1F7A5A] uppercase tracking-wider mb-3">
                  Session Facilitator
                </p>
                
                {/* Compact Facilitator Card - Girish Mehta */}
                <div className="bg-white rounded-lg p-3 border border-[#E5E7EB]" data-testid="facilitator-card-1">
                  <div className="flex gap-3 items-center">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_fa466eb8-6701-4b93-b151-474047be8b48/artifacts/ib95i8uf_image.png"
                      alt="Girish Mehta"
                      className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[#4A4A4A] text-sm">Girish Mehta</p>
                      <p className="text-[#6B7280] text-xs">Co-Founder, CLiC · Care-Leaver Advocate</p>
                      <p className="text-[#9CA3AF] text-[10px] mt-0.5">Engages with corporates, governments, and foundations on aftercare programs.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="flex flex-col items-center">
                <Button
                  onClick={() => navigate("/session")}
                  variant="outline"
                  className="border-[#1F7A5A] bg-[#1F7A5A]/10 text-[#1F7A5A] hover:bg-[#1F7A5A] hover:text-white font-medium text-sm h-10 px-6 transition-colors"
                  data-testid="join-live-session-btn"
                >
                  Join the CSR Orientation Session
                </Button>
                <p className="text-[10px] text-[#6B7280] mt-3 text-center max-w-md">
                  A live 45-minute institutional overview led by CLiC's co-founders, outlining current state-aligned implementation opportunities.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* POST-18 TRANSITION GAP - Full Width Framing Section */}
        <div className="mt-24 pt-12 border-t border-[#E5E7EB]/50" data-testid="transition-gap-section">
          <div className="max-w-5xl mx-auto">
            <JourneyVisual />
          </div>
        </div>
      </div>

      {/* Modal Form - CSR Partnership Inquiry */}
      <Dialog open={isModalOpen} onOpenChange={(open) => { setIsModalOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-[#1F7A5A]">{formTitle}</DialogTitle>
            <p className="text-sm text-[#6B7280] leading-relaxed">
              This brief form helps us understand your organisation's CSR focus and align the discussion accordingly.
              <br />
              <span className="text-[#1F7A5A] font-medium">A member of the CLiC leadership team will respond within 2 working days.</span>
            </p>
          </DialogHeader>
          
          {!submitted ? (
            <form onSubmit={handleSubmit} className="space-y-4 mt-2" data-testid="csr-inquiry-form">
              {/* Organisation Name */}
              <div className="space-y-1.5">
                <Label htmlFor="organization" className="text-sm font-medium">Organisation Name <span className="text-red-500">*</span></Label>
                <Input
                  id="organization"
                  placeholder="Company / Foundation name"
                  value={formData.organization}
                  onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                  required
                  className="h-10 form-input-focus"
                  data-testid="input-organization"
                />
              </div>

              {/* Your Name */}
              <div className="space-y-1.5">
                <Label htmlFor="name" className="text-sm font-medium">Your Name <span className="text-red-500">*</span></Label>
                <Input
                  id="name"
                  placeholder="Full name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="h-10 form-input-focus"
                  data-testid="input-name"
                />
              </div>

              {/* Designation */}
              <div className="space-y-1.5">
                <Label htmlFor="designation" className="text-sm font-medium">Designation <span className="text-red-500">*</span></Label>
                <Select 
                  value={formData.designation} 
                  onValueChange={(value) => setFormData({ ...formData, designation: value })}
                  required
                >
                  <SelectTrigger className="h-10" data-testid="select-designation">
                    <SelectValue placeholder="Select your role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CSR Head">CSR Head</SelectItem>
                    <SelectItem value="CSR Committee Member">CSR Committee Member</SelectItem>
                    <SelectItem value="Sustainability / ESG Lead">Sustainability / ESG Lead</SelectItem>
                    <SelectItem value="Corporate Foundation">Corporate Foundation</SelectItem>
                    <SelectItem value="Finance / Compliance">Finance / Compliance</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Official Email */}
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-sm font-medium">Official Email <span className="text-red-500">*</span></Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="h-10 form-input-focus"
                  data-testid="input-email"
                />
              </div>

              {/* CSR Allocation Range */}
              <div className="space-y-1.5">
                <Label htmlFor="csrAllocation" className="text-sm font-medium">Indicative CSR Allocation Range <span className="text-red-500">*</span></Label>
                <Select 
                  value={formData.csrAllocation} 
                  onValueChange={(value) => setFormData({ ...formData, csrAllocation: value })}
                  required
                >
                  <SelectTrigger className="h-10" data-testid="select-allocation">
                    <SelectValue placeholder="Select allocation range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Below ₹50 lakh">Below ₹50 lakh</SelectItem>
                    <SelectItem value="₹50–75 lakh">₹50–75 lakh</SelectItem>
                    <SelectItem value="₹75 lakh–₹1 crore">₹75 lakh–₹1 crore</SelectItem>
                    <SelectItem value="Above ₹1 crore">Above ₹1 crore</SelectItem>
                    <SelectItem value="Exploring alignment">Exploring alignment</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Geography of Interest */}
              <div className="space-y-1.5">
                <Label htmlFor="geography" className="text-sm font-medium">Geography of Interest <span className="text-red-500">*</span></Label>
                <Select 
                  value={formData.geography} 
                  onValueChange={(value) => setFormData({ ...formData, geography: value })}
                  required
                >
                  <SelectTrigger className="h-10" data-testid="select-geography">
                    <SelectValue placeholder="Select geography" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Rajasthan">Rajasthan</SelectItem>
                    <SelectItem value="West Bengal">West Bengal</SelectItem>
                    <SelectItem value="Other State">Other State</SelectItem>
                    <SelectItem value="Multi-State / National">Multi-State / National</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Preferred Engagement Timeline */}
              <div className="space-y-1.5">
                <Label htmlFor="timeline" className="text-sm font-medium">Preferred Engagement Timeline <span className="text-red-500">*</span></Label>
                <Select 
                  value={formData.timeline} 
                  onValueChange={(value) => setFormData({ ...formData, timeline: value })}
                  required
                >
                  <SelectTrigger className="h-10" data-testid="select-timeline">
                    <SelectValue placeholder="Select timeline" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Within current CSR cycle">Within current CSR cycle</SelectItem>
                    <SelectItem value="Next 3–6 months">Next 3–6 months</SelectItem>
                    <SelectItem value="Exploring future alignment">Exploring future alignment</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Optional Notes */}
              <div className="space-y-1.5">
                <Label htmlFor="notes" className="text-sm font-medium">Optional Notes</Label>
                <Textarea
                  id="notes"
                  placeholder="Any specific requirements or questions..."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value.slice(0, 250) })}
                  className="min-h-[70px] form-input-focus resize-none"
                  data-testid="input-notes"
                  maxLength={250}
                />
                <p className="text-[10px] text-[#9CA3AF] text-right">{formData.notes.length}/250</p>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-11 bg-[#1F7A5A] hover:bg-[#145A43] text-white font-semibold rounded-lg"
                data-testid="submit-btn"
              >
                {submitting ? (
                  <span className="flex items-center gap-2">
                    <div className="spinner w-4 h-4"></div>
                    Submitting...
                  </span>
                ) : (
                  "Request Partnership Discussion"
                )}
              </Button>
            </form>
          ) : (
            /* Post-Submission Confirmation Screen */
            <div className="text-center py-6" data-testid="submission-success">
              <div className="w-14 h-14 bg-[#1F7A5A]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-7 h-7 text-[#1F7A5A]" />
              </div>
              <h3 className="font-display text-xl font-bold text-[#4A4A4A] mb-2">
                Thank you for your interest.
              </h3>
              <p className="text-[#6B7280] text-sm leading-relaxed mb-6">
                A member of the CLiC leadership team will review your submission and reach out within 2 working days.
                <br /><br />
                In the meantime, you may review our CSR partnership formats or join an upcoming live CSR orientation session.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  variant="outline"
                  onClick={() => scrollToSection('partnership-formats-section')}
                  className="border-[#1F7A5A] text-[#1F7A5A] hover:bg-[#1F7A5A]/5"
                  data-testid="view-formats-btn"
                >
                  View CSR Formats
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate("/session")}
                  className="border-[#1F7A5A] text-[#1F7A5A] hover:bg-[#1F7A5A]/5"
                  data-testid="join-session-btn"
                >
                  Join Live Session
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default HeroSection;
